import { FC, useState, useEffect, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { InputAdornment, IconButton } from '@material-ui/core'
import { TextFieldProps } from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import styled from 'styled-components'
import {
  useCoverLetterScreen_SearchForm_CoverLetterRoleListLazyQuery,
  CoverLetterStatus,
} from 'generated/graphql'
import { getCoverLetterTypeFromTab } from '@app.feature/coverletter/modules/getCoverLetterTypeFromTab'
import TextField from 'components/custom-material-ui/TextField'
import { TShowAutoCompleteType } from 'screens/CoverLetterScreen/mobile/components/SearchForm/Mui/SearchFormMobileMui'
import { FormData } from 'screens/CoverLetterScreen/types'
import { useSearchStateContext } from 'screens/CoverLetterScreen/useSearchState'
import CloseIcon from 'svgIcons/Close'
import PersonIcon from 'svgIcons/Person'

const useStyles = makeStyles(() => ({
  textField: {
    marginRight: 5,
    flex: 1,
    marginBottom: 10,
  },
  fieldIcon: {
    marginLeft: 16,
    height: 16,
    width: 16,
    color: '#333333',
  },
  autoCompletePaper: {
    border: '1px solid #ced4da',
  },
  closeIcon: {
    width: 10,
    height: 10,
  },
  textFieldInput: {
    backgroundColor: 'white',
  },

  autocompleteOption: {
    // nowarp for samsung/naver browser
    whiteSpace: 'nowrap',
  },
}))

interface RoleFieldMobileMuiProps {
  InputProps?: TextFieldProps['InputProps']
  className?: string
  showAutoComplete: TShowAutoCompleteType
  setShowAutoComplete: React.Dispatch<
    React.SetStateAction<TShowAutoCompleteType>
  >
}

const RoleFieldMobileMui: FC<RoleFieldMobileMuiProps> = (props) => {
  const classes = useStyles()
  const { tabState } = useSearchStateContext()

  const { InputProps: textFieldInputProps, className } = props

  const { register, watch, setValue, formState } = useFormContext<FormData>()

  const watchRole = watch('role')
  const { isSubmitting, touchedFields } = formState
  const roleTouched = touchedFields.role

  useEffect(() => {
    if (!roleTouched) {
      props.setShowAutoComplete('')
    }
  }, [roleTouched])

  const [roleList, setRoleList] = useState<any[] | null | undefined>(null)

  const [
    getRoleList,
    { data: roleData, loading: roleLoading, called, refetch },
  ] = useCoverLetterScreen_SearchForm_CoverLetterRoleListLazyQuery()

  const getRole = (value: any) => {
    if (called) {
      refetch({
        input: {
          query: value,
          status: CoverLetterStatus.PUBLISHED,
          types: getCoverLetterTypeFromTab(tabState),
        },
      })
    } else {
      getRoleList({
        variables: {
          input: {
            query: value,
            status: CoverLetterStatus.PUBLISHED,
            types: getCoverLetterTypeFromTab(tabState),
          },
        },
      })
    }
    props.setShowAutoComplete('role')
  }

  const handleChange = useCallback(
    (e: any) => {
      setValue('role', e.target.value, {
        shouldDirty: true,
      })
    },
    [setValue],
  )

  const handleOnChange = (_e, value: FormData) => {
    if (value) {
      setValue('role', value.role, {
        shouldDirty: true,
      })
      props.setShowAutoComplete('')
    }
  }

  const handleClearRoleField = () => {
    setValue('role', '', {
      shouldDirty: true,
    })
    props.setShowAutoComplete('')
  }

  useEffect(() => {
    register('role')
  }, [register])

  useEffect(() => {
    if (roleData?.coverLetterRoleList) {
      setRoleList(roleData?.coverLetterRoleList)
    }
  }, [roleData, roleList])

  const autoCompleteClasses = {
    root: className,
    paper: classes.autoCompletePaper,
    option: classes.autocompleteOption,
  }

  return (
    <Autocomplete
      open={props.showAutoComplete === 'role'}
      onClose={() => props.setShowAutoComplete('')}
      disabled={isSubmitting}
      id="cover-letter-autocomplete-role"
      getOptionLabel={(role) => role.role}
      options={roleList || []}
      loading={roleLoading}
      classes={autoCompleteClasses}
      inputValue={watchRole}
      filterOptions={(options) => options}
      noOptionsText="일치하는 직무가 없습니다."
      loadingText="불러오는중..."
      onChange={handleOnChange}
      // TODO: Update MUI first
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderInput={({ InputLabelProps, ...params }) => {
        return (
          <StyledWrapper>
            <TextField
              {...params}
              inputRef={register('role').ref}
              onChange={handleChange}
              fullWidth
              type="search"
              name="role"
              className={classes.textField}
              placeholder="직무"
              onDebounceChange={getRole}
              debounceDelayMS={roleTouched ? 500 : 0}
              disabled={isSubmitting}
              InputProps={{
                ...params.InputProps,
                ...textFieldInputProps,
                inputProps: {
                  ...params?.inputProps,
                  ...textFieldInputProps?.inputProps,
                },
                classes: { root: classes.textFieldInput },
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon className={classes.fieldIcon} />
                  </InputAdornment>
                ),
                endAdornment: watchRole ? (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearRoleField}>
                      <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </StyledWrapper>
        )
      }}
    />
  )
}

export default RoleFieldMobileMui

const StyledWrapper = styled.div`
  input {
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }
  }
`
